import {
  NativeBiometric,
  BiometryType,
  AccessControl,
} from '@capgo/capacitor-native-biometric';
import { supabase } from './supabase';
import { errorMessage } from './errorMessage';
import { isNativeApp } from './native';
import { invokeFunction } from './invokeFunction';
import {
  BIOMETRY, labelForBiometryType, isCancellation, type BiometricLabel,
} from './biometricRules';

/* =============================================================================
 * Biometric sign-in
 * -----------------------------------------------------------------------------
 * What actually happens, because "fingerprint login" describes two very
 * different things and only one of them is this.
 *
 * The fingerprint or face is never seen by this app and never leaves the
 * handset. Android's own BiometricPrompt checks it against what the owner
 * enrolled in the operating system, and on success releases a key held in the
 * hardware keystore. That key decrypts one thing: the Supabase refresh token
 * we stored when the user turned this on. We hand that token to Supabase, get a
 * session back, and the user is in.
 *
 * So the biometric is not a credential we verify — it is a lock on a credential
 * the device is holding for us. Nothing biometric is transmitted, stored on our
 * servers, or recoverable from our database, which is the whole reason to do it
 * this way rather than matching faces server-side.
 *
 * `getSecureCredentials` matters specifically: it binds the prompt to a
 * CryptoObject, so the biometric is cryptographically tied to releasing the
 * decryption key. A plain "prompt, then read the file" design is a UI check
 * that a rooted device can step around.
 * ========================================================================== */

/** Keystore entry name. Changing it orphans every enrolment on every device. */
const SERVER = 'noorone-erp';

/**
 * Working around a bug in the plugin's Keystore parameters on Android 12+.
 *
 * `buildCredentialKey` calls `setUserAuthenticationParameters(timeout, types)`,
 * and on API 31+ passes its own constant `BIOMETRIC_STRONG | BIOMETRIC_WEAK`,
 * which it numbers 1 | 2 = 3. Android's KeyProperties number those flags
 * differently — `AUTH_DEVICE_CREDENTIAL = 1`, `AUTH_BIOMETRIC_STRONG = 2` — so
 * what actually reaches the Keystore is "device credential + biometric".
 *
 * With a timeout of 0, that combination is illegal: device-credential
 * authorisation is inherently time-based. The key is created anyway, the prompt
 * succeeds, and then `cipher.doFinal()` throws a KeyStoreException carrying no
 * message at all — surfacing as "Failed to encrypt credentials: null".
 *
 * A non-zero validity duration makes the combination legal. Ten seconds is long
 * enough for the cipher operation that follows the prompt and short enough that
 * the key is not left usable in any meaningful sense.
 */
const AUTH_VALIDITY_SECONDS = 10;

export type { BiometricLabel };

export interface BiometricStatus {
  /** The handset has usable biometrics, and we are running inside the app. */
  available: boolean;
  /** This account has already turned it on, on this device. */
  enrolled: boolean;
  /** For wording the button: "fingerprint" reads wrong on a Face ID phone. */
  label: BiometricLabel;
  /**
   * True when the only enrolled biometric is one Android classes as weak —
   * typically camera face unlock. It still signs the user in; it is worth
   * knowing because a weak biometric cannot release a hardware-backed key on
   * older Android, so enrolment may take the fallback path.
   */
  weakOnly: boolean;
}

const UNAVAILABLE: BiometricStatus = {
  available: false,
  enrolled: false,
  label: 'biometrics',
  weakOnly: false,
};

/*
 * The label and cancellation rules live in biometricRules.ts so they can be
 * tested without a handset. This asserts the plain numbers used there still
 * match the plugin's enum — the same compile-time check policy.ts uses to keep
 * its schema and its interface in step.
 */
type Assert<T extends true> = T;
export type _BiometryMatches = Assert<
  BiometryType.FINGERPRINT extends typeof BIOMETRY.FINGERPRINT
    ? BiometryType.FACE_ID extends typeof BIOMETRY.FACE_ID
      ? BiometryType.MULTIPLE extends typeof BIOMETRY.MULTIPLE
        ? true
        : false
      : false
    : false
>;

/**
 * Can this device do it, and has this user already set it up?
 *
 * Never throws. Every caller uses the answer to decide whether to show a
 * button, and a status check that can explode would take the login screen with
 * it on some unknown handset.
 */
export async function biometricStatus(): Promise<BiometricStatus> {
  if (!isNativeApp()) return UNAVAILABLE;

  try {
    // `useFallback: false` deliberately. Device PIN is not what was asked for,
    // and offering "sign in with biometrics" that opens a PIN pad is a lie.
    const result = await NativeBiometric.isAvailable({ useFallback: false });
    if (!result.isAvailable) return UNAVAILABLE;

    const saved = await NativeBiometric.isCredentialsSaved({ server: SERVER });

    return {
      available: true,
      enrolled: saved.isSaved,
      label: labelForBiometryType(result.biometryType),
      weakOnly: !result.strongBiometryIsAvailable,
    };
  } catch {
    return UNAVAILABLE;
  }
}

/**
 * Turns it on for the signed-in user.
 *
 * Called with a live session, so there is a refresh token worth keeping. The
 * identity check first is not ceremony: it proves the person enrolling is the
 * one holding the phone, rather than someone who picked it up mid-session.
 */
export async function enableBiometric(
  email: string,
  deviceSecret: string,
  label: BiometricStatus['label'],
): Promise<void> {
  // No separate verifyIdentity() first.
  //
  // setCredentials with accessControl shows its own prompt, so calling
  // verifyIdentity beforehand asked the user for the same finger twice for one
  // action — which is what it looked like, because it is what it was.
  const stored = () =>
    NativeBiometric.setCredentials({
      username: email,
      password: deviceSecret,
      server: SERVER,
      // BIOMETRY_ANY rather than BIOMETRY_CURRENT_SET.
      //
      // CURRENT_SET destroys the stored token the moment any fingerprint is
      // added or removed, which across a thousand handsets means a steady
      // trickle of men whose sign-in silently stopped with no explanation. The
      // security it buys is thin: enrolling a new fingerprint needs the device
      // PIN, and anyone with that already owns the phone.
      accessControl: AccessControl.BIOMETRY_ANY,
      authValidityDuration: AUTH_VALIDITY_SECONDS,
      title: 'NoorOne ERP',
      negativeButtonText: 'Cancel',
    });

  try {
    await stored();
    return;
  } catch (caught) {
    const message = caught instanceof Error ? caught.message : String(caught);

    // A cancellation is the user's decision — never retry around it, or Cancel
    // becomes a button that does nothing.
    if (isCancellation(message)) throw caught;

    // Keystore parameter rejections vary by manufacturer; the plugin already
    // carries workarounds for Xiaomi and Oppo doing this differently. Rather
    // than enumerate handsets, fall back once to a key with no hardware
    // auth-binding and gate retrieval on an explicit prompt instead.
    //
    // Weaker, honestly: the token is encrypted at rest but the key is not
    // itself locked by the fingerprint, so a rooted device could reach it.
    // Still far better than a password typed on a building site, and it is
    // only reached when the strong path has already failed on this handset.
    await NativeBiometric.verifyIdentity({
      reason: 'Confirm it is you before turning this on',
      title: 'NoorOne ERP',
      subtitle: `Enable ${label} sign-in`,
      negativeButtonText: 'Cancel',
    });

    await NativeBiometric.setCredentials({
      username: email,
      password: deviceSecret,
      server: SERVER,
      title: 'NoorOne ERP',
      negativeButtonText: 'Cancel',
    });
  }
}

/**
 * Forgets this device. The account is untouched; only this handset is.
 *
 * Revokes server-side first, then clears the keystore. That order matters: if
 * only the local copy were removed, a secret already extracted from the device
 * would still be accepted. Revoking first means a lost phone is locked out even
 * if the local delete never runs.
 */
export async function disableBiometric(): Promise<void> {
  try {
    const { data } = await supabase
      .from('device_credentials')
      .select('id')
      .limit(20);

    // The delete policy scopes this to the caller's own rows, so no filter
    // beyond that is needed — and an admin revoking somebody else's device does
    // it from the Users screen, not here.
    for (const row of data ?? []) {
      await supabase.from('device_credentials').delete().eq('id', row.id);
    }
  } catch {
    // Signed out already, or offline. The local delete below still runs, and a
    // stale row is harmless until it is used — at which point the server will
    // still find it, which is why this is attempted first.
  }

  if (!isNativeApp()) return;
  try {
    await NativeBiometric.deleteCredentials({ server: SERVER });
  } catch {
    /* nothing stored, which is the state we wanted */
  }
}

export type UnlockResult =
  | { ok: true }
  | { ok: false; cancelled: boolean; message: string };

/**
 * Signs in with the device secret the keystore is holding.
 *
 * Two steps, and the split is the point. The fingerprint releases the secret,
 * on the device, where the biometric never leaves. The server then decides
 * whether that secret still corresponds to a live enrolment — so revoking a
 * lost handset takes effect immediately and does not depend on the handset
 * co-operating.
 */
export async function unlockWithBiometric(): Promise<UnlockResult> {
  if (!isNativeApp()) {
    return { ok: false, cancelled: false, message: 'Only available in the app.' };
  }

  let credentials: { username: string; password: string };

  try {
    // The hardware-bound path: the prompt is tied to a CryptoObject, so the
    // fingerprint releases the decryption key rather than merely reporting a
    // success the app could be tricked into believing.
    credentials = await NativeBiometric.getSecureCredentials({
      server: SERVER,
      reason: 'Sign in to NoorOne ERP',
      title: 'NoorOne ERP',
      subtitle: 'Confirm your identity',
      negativeButtonText: 'Use password',
    });
  } catch (secureError) {
    const secureMessage =
      secureError instanceof Error ? secureError.message : String(secureError);

    if (isCancellation(secureMessage)) {
      return { ok: false, cancelled: true, message: 'Cancelled.' };
    }

    // Enrolment may have fallen back to unbound storage on this handset, in
    // which case getSecureCredentials has nothing to read. Prompt explicitly,
    // then read the plain entry. Trying this second means the strong path is
    // always preferred where it works.
    try {
      await NativeBiometric.verifyIdentity({
        reason: 'Sign in to NoorOne ERP',
        title: 'NoorOne ERP',
        subtitle: 'Confirm your identity',
        negativeButtonText: 'Use password',
      });
      credentials = await NativeBiometric.getCredentials({ server: SERVER });
    } catch (plainError) {
      const message =
        plainError instanceof Error ? plainError.message : String(plainError);
      const cancelled = isCancellation(message);
      return {
        ok: false,
        cancelled,
        message: cancelled ? 'Cancelled.' : message,
      };
    }
  }

  // Exchange the device secret for a session.
  //
  // Nothing is stored back afterwards: the secret does not rotate, so the copy
  // in the keystore stays valid for as long as the enrolment does. That is the
  // whole reason it replaced the refresh token.
  let tokenHash: string;

  try {
    const result = await invokeFunction<{ tokenHash?: string }>('biometric-auth', {
      body: { action: 'verify', secret: credentials.password },
    });
    if (!result?.tokenHash) throw new Error('That device is no longer enrolled.');
    tokenHash = result.tokenHash;
  } catch (caught) {
    const message = caught instanceof Error ? caught.message : String(caught);

    // Only forget the device when the server says the enrolment is gone —
    // revoked, or the account deleted. A network failure on a building site
    // must not silently un-enrol a handset the man will need tomorrow.
    if (/no longer enrolled|not enrolled|no longer exists/i.test(message)) {
      await disableBiometric();
      return {
        ok: false,
        cancelled: false,
        message: 'This device is no longer enrolled. Sign in with your password to set it up again.',
      };
    }

    return { ok: false, cancelled: false, message };
  }

  const { error } = await supabase.auth.verifyOtp({
    token_hash: tokenHash,
    type: 'email',
  });

  if (error) {
    /* Through the funnel: this reaches the sign-in screen, and GoTrue writes
       for a developer. See errorMessage. */
    return { ok: false, cancelled: false, message: errorMessage(error, 'sign in with biometrics') };
  }

  // Recorded explicitly, because nothing else will. The sign-in trigger watches
  // auth.users.last_sign_in_at, and a token refresh does not touch it — so
  // without this, every biometric sign-in would be invisible in the log.
  void supabase.rpc('record_signin', { p_method: 'biometric' }).then(({ error: rpcError }) => {
    if (rpcError) console.warn('Could not record the sign-in', rpcError.message);
  });

  return { ok: true };
}
